import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Reusable store-stock badge + dropdown.
 *
 * Props (search results shape):
 *   homeQty   – number of units at home/main store
 *   stores    – [{ id, name, quantity, type }]  (from normalizeProducts)
 *
 * Props (cart shape):
 *   availabilityLines – [{ name, quantity, type }]
 *   homeName          – order.store.name  (used to identify the home-store row)
 */
export default function PartsTechStoreStock({ homeQty, stores, availabilityLines, homeName, stopPropagation = true }) {
    const [open, setOpen]       = useState(false);
    const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
    const badgeRef              = useRef(null);

    // Close on scroll or resize so it doesn't float away
    useEffect(() => {
        if (!open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, true);
        window.addEventListener('resize', close);
        return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
    }, [open]);

    // ── Normalise into a single list ──────────────────────────────────────────
    // Search-results mode: `stores` array is already split into MAIN / CLOSEST / etc.
    // Cart mode: `availabilityLines` is a flat list; match by name to find home store.
    let homeStore  = null;
    let otherLines = [];

    if (stores && stores.length > 0) {
        homeStore  = stores.find(s => s.type === 'MAIN') || null;
        otherLines = stores.filter(s => s.type !== 'MAIN');
    } else if (availabilityLines && availabilityLines.length > 0) {
        // Cart context — identify home store by matching name
        const home = availabilityLines.find(l =>
            homeName && l.name && l.name.toLowerCase() === homeName.toLowerCase()
        );
        homeStore  = home || availabilityLines[0];          // fall back to first row
        otherLines = availabilityLines.filter(l => l !== homeStore);
    }

    const qty      = homeQty != null ? homeQty : (homeStore?.quantity ?? 0);
    const hasLines = (stores?.length > 0) || (availabilityLines?.length > 0);

    function toggle(e) {
        if (stopPropagation) e.stopPropagation();
        if (!open && badgeRef.current) {
            const rect = badgeRef.current.getBoundingClientRect();
            setDropPos({ top: rect.bottom + 4, left: rect.left });
        }
        setOpen(v => !v);
    }

    function close(e) {
        if (stopPropagation && e) e.stopPropagation();
        setOpen(false);
    }

    const dropdown = open && hasLines && ReactDOM.createPortal(
        <div
            onClick={e => e.stopPropagation()}
            style={{
                position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999,
                minWidth: 290,
                background: '#fff', border: '1px solid #ccc', borderRadius: 6,
                boxShadow: '0 4px 16px rgba(0,0,0,0.18)', overflow: 'hidden',
            }}
        >
            {/* Header */}
            <div style={{ background: '#343a40', color: '#fff', padding: '6px 12px', display: 'flex', justifyContent: 'space-between', fontSize: '0.8em', fontWeight: 600 }}>
                <span>Store Availability</span>
                <span style={{ cursor: 'pointer', opacity: 0.7 }} onClick={close}>✕</span>
            </div>

            {/* Home store */}
            {homeStore && (
                <div style={{ padding: '7px 12px', borderBottom: '1px solid #eee', background: '#f0faf0' }}>
                    <div style={{ fontSize: '0.7em', fontWeight: 700, color: '#555', textTransform: 'uppercase', marginBottom: 2 }}>Your Store</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.85em' }}>{homeStore.name}</span>
                        <span style={{ fontWeight: 700, fontSize: '0.85em', color: homeStore.quantity > 0 ? '#155724' : '#721c24' }}>
                            {homeStore.quantity > 0 ? `● ${homeStore.quantity} avail.` : '● 0 avail.'}
                        </span>
                    </div>
                </div>
            )}

            {/* Other stores */}
            {otherLines.length > 0 && (
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                    <div style={{ padding: '4px 12px', fontSize: '0.7em', fontWeight: 700, color: '#555', textTransform: 'uppercase', background: '#fafafa', borderBottom: '1px solid #eee' }}>
                        Nearby / Network
                    </div>
                    {otherLines.map((s, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '0.84em' }}>
                            <span style={{ color: '#333' }}>{s.name}</span>
                            <span style={{ fontWeight: 600, color: s.quantity > 0 ? '#155724' : '#aaa', marginLeft: 12, whiteSpace: 'nowrap' }}>
                                {s.quantity > 0 ? `● ${s.quantity}` : '○ 0'}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>,
        document.body
    );

    return (
        <div style={{ display: 'inline-block' }}>
            {/* ── Badge trigger ── */}
            <div
                ref={badgeRef}
                onClick={hasLines ? toggle : undefined}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    cursor: hasLines ? 'pointer' : 'default',
                    background: qty > 0 ? '#d4edda' : '#f8d7da',
                    color:      qty > 0 ? '#155724' : '#721c24',
                    border:    `1px solid ${qty > 0 ? '#c3e6cb' : '#f5c6cb'}`,
                    borderRadius: 4, padding: '2px 8px',
                    fontSize: '0.82em', fontWeight: 600,
                    userSelect: 'none',
                }}
            >
                {qty > 0 ? <>{qty} in stock</> : <> Out of stock</>}
                {hasLines && <span style={{ opacity: 0.55, fontSize: '0.85em' }}>{open ? '▲' : '▼'}</span>}
            </div>

            {/* ── Dropdown (portal) ── */}
            {dropdown}
        </div>
    );
}
